import React from "react";
import { useCallback, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, Loader2 } from "lucide-react";
import type { CellProps } from "../types";

interface SelectCellProps extends CellProps<string> {
  options: { id: string; name: string }[];
}

export function SelectCell({
  value,
  onChange,
  options = [],
  disabled,
  placeholder = "Select...",
}: SelectCellProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  // Show "Loading..." for up to 15s when options are empty
  const [isWaitingForSync, setIsWaitingForSync] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsWaitingForSync(false), 30000);
    return () => clearTimeout(timer);
  }, []);

  // Reset waiting state when options arrive
  useEffect(() => {
    if (options.length > 0) setIsWaitingForSync(false);
  }, [options.length]);

  const isEmpty = options.length === 0;
  const showLoading = isEmpty && isWaitingForSync;

  const selectedOption = options.find((opt) => opt.id === value);

  if (showLoading) {
    return (
      <div className="flex items-center gap-1.5 px-2 h-8 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <select
        value={value ?? ""}
        onChange={handleChange}
        disabled={disabled || isEmpty}
        className={cn(
          "w-full h-8 px-2 pr-7 text-sm bg-transparent rounded appearance-none",
          "border border-transparent transition-colors cursor-pointer",
          "hover:border-border focus:border-primary focus:outline-none",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          !value && "text-muted-foreground/70"
        )}
      >
        <option value="" disabled>
          {isEmpty ? "No options" : placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id} className="bg-popover text-popover-foreground">
            {opt.name}
          </option>
        ))}
      </select>
      <ChevronDown
        className={cn(
          "absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none",
          "text-muted-foreground"
        )}
      />
    </div>
  );
}
